import { useTranslation } from 'react-i18next'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { cn } from '@/lib/utils'

export interface CostCardProps {
  respondentCount: number
  costPerRespondent: number
  totalCost: number
  /** Rendered below the cost breakdown — typically a CreditStatusBadge. */
  creditStatus: React.ReactNode
  /** Optional content below the credit status (e.g. marketplace note, validation errors). */
  footer?: React.ReactNode
  className?: string
}

/**
 * Sticky cost summary card used in confirmation/review steps.
 * Shows respondent count, per-unit cost, total, and a credit status slot.
 * Shared between study ReviewStep and audience ConfirmationStep.
 */
export function CostCard({
  respondentCount,
  costPerRespondent,
  totalCost,
  creditStatus,
  footer,
  className,
}: CostCardProps) {
  const { t } = useTranslation('app')

  return (
    <div className={cn('w-full md:w-[320px] md:flex-shrink-0', className)}>
      <div className="bg-card rounded-2xl p-7 space-y-5 md:sticky md:top-4">
        <h3 className="text-lg font-medium text-foreground dark:text-white">
          {t('billing.costCard.title')}
        </h3>

        <div className="space-y-2">
          <div className="rounded-xl flex justify-between items-end">
            <span className="text-sm text-muted-foreground dark:text-muted-foreground">
              {t('billing.costCard.respondents')}
            </span>
            <span className="text-base font-semibold text-foreground dark:text-white">{respondentCount}</span>
          </div>
          <div className="rounded-xl flex justify-between items-end">
            <span className="text-sm text-muted-foreground dark:text-muted-foreground">
              {t('billing.costCard.costPerRespondent')}
            </span>
            <span className="text-base font-semibold text-foreground dark:text-white">
              {t('billing.costCard.credits', { count: costPerRespondent })}
            </span>
          </div>
          <div className="h-px bg-border" />
          <div className="rounded-xl flex justify-between items-end">
            <span className="text-sm font-medium text-foreground dark:text-white flex items-center gap-1.5">
              <CreditIcon className="h-4 w-4" />
              {t('billing.costCard.total')}
            </span>
            <span className="text-base font-semibold text-foreground dark:text-white">
              {t('billing.costCard.credits', { count: totalCost })}
            </span>
          </div>
        </div>

        {creditStatus}

        {footer}
      </div>
    </div>
  )
}
